import React, { useEffect, useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Text from '~/components/core/TextComponents/Text';
// import SubOrganizationSelect from '~/components/TPA/SubOrganizations/SubOrganizationSelect';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useLobOptions from '~/components/TPA/LOB/hooks/useLobOptions';
import type { TemplateRequest } from '~/server_shared/openapi/data-contracts';
import { getLobDescription } from '~/Utils/lobUtils';

import { usePolicyTemplates } from '../hooks/usePolicyTemplates';
import type { LineOfBusiness, PolicyTemplateResponse, SubOrganizationInfo } from '../types/policy.types';

interface PolicyTemplateSelectorProps {
  onTemplateSelected: (template: PolicyTemplateResponse | null) => void;
  disabled?: boolean;
  initialLob?: LineOfBusiness;
  initialSubOrgId?: string;
}

const PolicyTemplateSelector: React.FC<PolicyTemplateSelectorProps> = ({
  onTemplateSelected,
  disabled = false,
  initialLob,
  initialSubOrgId,
}) => {
  const { organization } = useSysconfig() as { organization: { sub_organizations?: SubOrganizationInfo[] } };
  const { getTemplate, loading, error } = usePolicyTemplates();
  const { lobConfigurationsDict } = useLobConfiguration();

  // State for form fields
  const [selectedLob, setSelectedLob] = useState<LineOfBusiness | ''>('');
  const [selectedSubOrgId, setSelectedSubOrgId] = useState<string | ''>('');

  // Helper to get LOB display name
  const getLobDisplayName = (lob: string): string => {
    return getLobDescription(lob.replace('_policy', '_claim'), lobConfigurationsDict) || lob;
  };

  // Convert external ID to internal ID for LOB API (which expects internal IDs)
  const selectedSubOrgInternalId = React.useMemo(() => {
    if (!selectedSubOrgId || !organization?.sub_organizations) return null;
    const subOrg = organization.sub_organizations.find((so) => so.external_id === selectedSubOrgId);
    return subOrg ? subOrg.id : null;
  }, [selectedSubOrgId, organization?.sub_organizations]);

  // Get available LOBs for the selected sub-organization (using internal ID)
  const { lobOptions, isLoading: isLoadingLobs } = useLobOptions({
    subOrganizationIds: selectedSubOrgInternalId ? [selectedSubOrgInternalId] : null,
  });

  // Initialize with props
  useEffect(() => {
    if (initialLob) {
      setSelectedLob(initialLob);
    }
    if (initialSubOrgId) {
      setSelectedSubOrgId(initialSubOrgId);
    }
  }, [initialLob, initialSubOrgId]);

  // Handle LOB selection
  const handleLobChange = (lob: LineOfBusiness) => {
    setSelectedLob(lob);
    onTemplateSelected(null);
  };

  // Handle sub-organization selection
  const handleSubOrgChange = (subOrgId: string) => {
    setSelectedSubOrgId(subOrgId);
    setSelectedLob(''); // Clear LOB when sub-org changes since LOBs are specific to sub-org
    onTemplateSelected(null);
  };

  // Fetch template and notify parent
  const handleSelectTemplate = async () => {
    if (!selectedLob) return;

    try {
      const request: TemplateRequest = {
        lob: selectedLob,
        sub_organization_external_id: selectedSubOrgId || undefined, // Use external_id directly
      };

      const template = await getTemplate(request);

      if (onTemplateSelected) {
        onTemplateSelected(template);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch template:', err);
      // For now, just log the error - the hook should handle 404s with mock data
    }
  };

  // Always show selection interface with clear section header
  return (
    <div className="space-y-3">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="text-gray-700">
        Select Policy Template
      </Text>
      <Grid container spacing={2} alignItems="flex-end">
        {/* Sub-Organization Selection */}
        <Grid item xs={4}>
          {organization?.sub_organizations && organization.sub_organizations.length > 0 ? (
            <SingleSelectField
              id="sub-org-selection"
              label="Sub-Organization"
              value={selectedSubOrgId}
              onChange={(newValue) => handleSubOrgChange(newValue as string)}
              disabled={disabled}
              fullWidth
              InputLabelProps={{ htmlFor: 'sub-org-selection' }}
            >
              {organization.sub_organizations.map((subOrg) => (
                <MenuItem key={subOrg.id} value={subOrg.external_id}>
                  {subOrg.name}
                </MenuItem>
              ))}
            </SingleSelectField>
          ) : (
            <SingleSelectField
              id="sub-org-disabled"
              label="Sub-Organization"
              value="default"
              disabled
              fullWidth
              InputLabelProps={{ htmlFor: 'sub-org-disabled' }}
            >
              <MenuItem value="default">Default</MenuItem>
            </SingleSelectField>
          )}
        </Grid>

        {/* LOB Selection */}
        <Grid item xs={4}>
          <SingleSelectField
            id="lob-selection"
            label="Line of Business"
            value={selectedLob}
            onChange={(newValue) => handleLobChange(newValue as LineOfBusiness)}
            disabled={
              disabled ||
              isLoadingLobs ||
              lobOptions.length === 0 ||
              (organization?.sub_organizations && organization.sub_organizations.length > 0 && !selectedSubOrgId)
            }
            fullWidth
            InputLabelProps={{ htmlFor: 'lob-selection' }}
          >
            {lobOptions.map((lob: string) => (
              <MenuItem key={lob} value={lob}>
                {getLobDisplayName(lob)}
              </MenuItem>
            ))}
          </SingleSelectField>
        </Grid>

        {/* Select Template Button */}
        <Grid item xs={4} className="flex items-end">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSelectTemplate}
            disabled={
              disabled ||
              !selectedLob ||
              loading ||
              isLoadingLobs ||
              (organization?.sub_organizations && organization.sub_organizations.length > 0 && !selectedSubOrgId)
            }
          >
            {loading || isLoadingLobs ? 'Loading...' : 'Select Template'}
          </Button>
        </Grid>

        {/* Error Display - Full width below the row */}
        {error && (
          <Grid item xs={12}>
            <Text variant={Text.VARIANTS.SM} className="text-red-600">
              Error: {error}
            </Text>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default PolicyTemplateSelector;
